import React from "react";
import { tagsApi } from "api/instances";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";

import MoreOptionsIconButton from "modules/common/components/buttons/moreOptionsIconButton";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import MoreOptionsItem from "modules/common/components/moreOptionsItem";

import { TopicDetails, Topic } from "../../models";
import { Typography } from "@mui/material";
import { TopicDeletionHandler } from "modules/topics/utils/topicDeletionHandler";
import TopicEdit from "../dialogs/topicEdit";
import TopicMove from "../dialogs/topicMove";
import { TopicReportHandler } from "modules/topics/utils/topicReportHandler";
import { ReportTypes } from "modules/reports/models";
import SuccessSnackbar from "modules/common/components/snackbars/successSnackbar";
import TopicViewDetails from "../dialogs/details/topicViewDetails";
import { BasicAudience } from "modules/audiences";
import { editTopicState } from "modules/topics/reducer";
import { SNACKBAR_TIME_MS } from "modules/topics/styles/constants";

const MoreTopicOptions: React.FunctionComponent<PropsWithRedux> = props => {
    const [editDialogOn, setEditDialogOn] = React.useState(false);
    const [moveDialogOn, setMoveDialogOn] = React.useState(false);
    const [topicDetailsOn, setTopicDetailsOn] = React.useState(false);
    const [topicDetails, setTopicDetails] = React.useState<TopicDetails | undefined>(undefined);

    const [successMessage, setSuccessMessage] = React.useState("");

    const dispatch = useDispatch();

    const openMoveDialog = () => setMoveDialogOn(true);

    const closeMoveDialog = () => setMoveDialogOn(false);

    const closeEditDialog = () => setEditDialogOn(false);

    const closeDetailsDialog = () => setTopicDetailsOn(false);

    const onHandleDelete = () => {
        const topics = { topics: [props.topic] };
        new TopicDeletionHandler(topics, dispatch).onHandleDelete();
    };

    const handleDownload = async () => {
        await new TopicReportHandler(props.topic.name, props.topic.id).requestReport(ReportTypes.TopicAllReport);
        setSuccessMessage(`Your report is being generated and will be emailed to ${props.currentUser?.email} when it's ready.`);
    };

    const handleStateUpdate = (disable: boolean) => {
        const topics = [props.topic.id];
        dispatch(editTopicState({ topics, disable }));
    };

    const openDetailsDialog = async () => {
        setTopicDetailsOn(true);
        await getTopicDetails();
    };

    const openTopicEditor = async () => {
        setEditDialogOn(true);
        await getTopicDetails();
    };

    const parseAudiences = (audienceIds: string[]): BasicAudience[] => {
        return audienceIds
            .map(id => ({
                id,
                name: props.audiences.find(a => a.id === id)?.displayName || ""
            }
            ));
    };

    const getTopicDetails = async () => {
        const details = await tagsApi.getTopicById(props.topic.id);

        const cleanedDetails = {
            id: props.topic.id,
            name: details.name,
            groupId: details.group,
            disabled: details.disabled,
            default: details.default,
            mandatory: details.mandatory,
            restricted: details.restricted,
            defaultFor: parseAudiences(details.defaultFor),
            mandatoryFor: parseAudiences(details.mandatoryFor),
            restrictedTo: parseAudiences(details.restrictedTo,)
        };
        setTopicDetails(cleanedDetails);
    };

    const deleteTopicText = (): JSX.Element =>
        <Typography className="red-text">
            Delete
        </Typography>;

    return (
        <React.Fragment>
            <SuccessSnackbar
                successMessage={successMessage}
                clearSuccessMessage={() => setSuccessMessage("")}
                autoHideDuration={SNACKBAR_TIME_MS}
            />
            <MoreOptionsIconButton>
                <List disablePadding>
                    <MoreOptionsItem
                        text="View details"
                        onClick={openDetailsDialog}
                    />
                    <Divider light />

                    <MoreOptionsItem
                        text="Edit"
                        onClick={openTopicEditor}
                    />

                    <MoreOptionsItem
                        text="Move topic"
                        onClick={openMoveDialog}
                    />

                    <MoreOptionsItem
                        text="Download all reports"
                        onClick={handleDownload}
                    />

                    <MoreOptionsItem
                        text={props.topic.disabled ? "Enable" : "Disable"}
                        onClick={() => handleStateUpdate(!props.topic.disabled)}
                    />

                    <Divider light />
                    <MoreOptionsItem
                        text={deleteTopicText()}
                        onClick={onHandleDelete}
                    />

                </List>
            </MoreOptionsIconButton>

            <TopicEdit
                show={editDialogOn}
                onClose={closeEditDialog}
                topic={topicDetails}
            />
            <TopicMove
                toMove={props.topic.name}
                topics={[props.topic.id]}
                show={moveDialogOn}
                onClose={closeMoveDialog}
            />
            <TopicViewDetails
                topic={topicDetails}
                show={topicDetailsOn}
                onEdit={openTopicEditor}
                onClose={closeDetailsDialog}
                onMove={openMoveDialog}
            />
        </React.Fragment>
    );
};

interface ComponentProps {
    topic: Topic;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        audiences: state.audiences.audiences,
        currentUser: state.settings.currentUser
    })
);

type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(MoreTopicOptions);